// src/components/Playground/hooks/sessions/useAutoSaveSession.ts
import { reaction } from "mobx";
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";

import { usePlayground } from "../../playground-store";

import { HydrationState } from "./useHydrateSession";

import { useUpdatePlaygroundSession } from "@/queries";
import { useAuth } from "@/context/auth-context";

export interface SaveCompletionTracker {
  waitForSaveCompletion: () => Promise<void>;
}

const useAutoSaveSession = (
  hydrationState: HydrationState,
  saveCompletionTracker?: React.MutableRefObject<SaveCompletionTracker | null>,
) => {
  const playground = usePlayground();
  const userToken = useAuth()?.userToken || "";
  const { mutate: updateSession, reset: resetSessionUpdate } =
    useUpdatePlaygroundSession(userToken);
  const queryClient = useQueryClient();

  // Track if a save is in progress
  const saveInProgressRef = useRef(false);
  // Track pending save for after current save completes
  const hasPendingSaveRef = useRef(false);
  // Track callers waiting for the latest save attempt to settle.
  const saveWaitersRef = useRef<{ resolve: () => void; reject: (reason?: unknown) => void }[]>([]);
  const saveErrorRef = useRef<unknown>(null);
  const changeVersionRef = useRef(0);
  const activeSaveVersionRef = useRef(0);
  const lastPersistedVersionRef = useRef(0);
  const scheduledSaveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    let disposed = false;
    const settleSaveWaiters = (error?: unknown) => {
      const waiters = saveWaitersRef.current;
      saveWaitersRef.current = [];
      waiters.forEach(waiter => {
        if (error) {
          waiter.reject(error);
          return;
        }
        waiter.resolve();
      });
    };

    // Function to perform the actual save
    const performSave = () => {
      if (disposed || playground.isPendingSessionInitialization || playground.isXRayMode) {
        return;
      }

      // If a save is already in progress, mark that we have a pending save
      if (saveInProgressRef.current) {
        if (changeVersionRef.current > activeSaveVersionRef.current) {
          hasPendingSaveRef.current = true;
        }
        return;
      }

      if (scheduledSaveTimeoutRef.current) {
        clearTimeout(scheduledSaveTimeoutRef.current);
        scheduledSaveTimeoutRef.current = null;
      }

      try {
        saveInProgressRef.current = true;
        saveErrorRef.current = null;
        activeSaveVersionRef.current = changeVersionRef.current;
        playground.setIsSavingSession(true);
        resetSessionUpdate();

        const chatProps = {
          mode: playground.playgroundSessionMode,
          ...(playground.isSessionModeChat ? { chat_settings: playground.chat_settings } : {}),
        };

        updateSession(
          {
            workspaceId: playground.workspace_id,
            session: {
              id: playground.instanceId,
              name: playground.sessionName ?? "",
              prompt_blueprint: playground.blueprint,
              input_variables: playground.input_variable_sets.map(set => set.variables),
              input_variable_set_names: playground.input_variable_set_names,
              snippet_overrides: playground.snippetOverrideStore.snippetOverrides,
              ...chatProps,
            },
          },
          {
            onSuccess: data => {
              if (disposed) {
                return;
              }
              saveErrorRef.current = null;
              lastPersistedVersionRef.current = activeSaveVersionRef.current;
              playground.setSessionUpdatedAt(data.playground_session.updated_at);
              queryClient.invalidateQueries({
                queryKey: ["playground-session", playground.workspace_id, playground.instanceId],
              });
            },
            onError: error => {
              if (!disposed) {
                saveErrorRef.current = error;
              }
            },
            onSettled: () => {
              if (disposed) {
                return;
              }

              saveInProgressRef.current = false;
              const hasChangesAfterSaveStarted =
                changeVersionRef.current > activeSaveVersionRef.current;

              // Keep the save barrier active while newer changes wait for persistence.
              if (hasPendingSaveRef.current || hasChangesAfterSaveStarted) {
                hasPendingSaveRef.current = false;
                scheduledSaveTimeoutRef.current = setTimeout(() => {
                  scheduledSaveTimeoutRef.current = null;
                  performSave();
                }, 100);
                return;
              }

              playground.setIsSavingSession(false);
              const hasUnpersistedChanges =
                lastPersistedVersionRef.current < changeVersionRef.current;
              settleSaveWaiters(hasUnpersistedChanges ? saveErrorRef.current : undefined);
            },
          },
        );
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to perform auto-save:", error);
        saveErrorRef.current = error;
        saveInProgressRef.current = false;
        playground.setIsSavingSession(false);
        settleSaveWaiters(error);
      }
    };

    const waitForSaveCompletion = () => {
      if (playground.isPendingSessionInitialization || playground.isXRayMode) {
        return Promise.resolve();
      }

      const isFullyPersisted = lastPersistedVersionRef.current >= changeVersionRef.current;
      if (
        isFullyPersisted &&
        !saveInProgressRef.current &&
        scheduledSaveTimeoutRef.current === null
      ) {
        return Promise.resolve();
      }

      const waitPromise = new Promise<void>((resolve, reject) => {
        saveWaitersRef.current.push({ resolve, reject });
      });

      if (saveInProgressRef.current) {
        if (changeVersionRef.current > activeSaveVersionRef.current) {
          hasPendingSaveRef.current = true;
        }
        return waitPromise;
      }

      if (scheduledSaveTimeoutRef.current === null) {
        performSave();
      }
      return waitPromise;
    };

    // Expose method to wait for save completion
    if (saveCompletionTracker) {
      saveCompletionTracker.current = {
        waitForSaveCompletion,
      };
    }
    playground.setWaitForSessionSaveCompletion(waitForSaveCompletion);

    // Reaction to set the saving indicator immediately when changes are detected
    const disposeUndebouncedReaction = reaction(
      () => [
        playground.payload,
        playground.messagesContent,
        playground.inputVariableSetsContent,
        playground.functionToolCallContent,
        playground.snippetOverrideStore.snippetOverridesContent,
        playground.playgroundSessionMode,
      ],
      () => {
        if (playground.isPendingSessionInitialization || playground.isXRayMode) {
          return;
        }

        changeVersionRef.current += 1;

        if (!playground.isSavingSession) {
          playground.setIsSavingSession(true);
        }
      },
    );

    // Debounced reaction to actually perform the save
    const disposeDebouncedReaction = reaction(
      () => [
        playground.payload,
        playground.messagesContent,
        playground.inputVariableSetsContent,
        playground.functionToolCallContent,
        playground.snippetOverrideStore.snippetOverridesContent,
        playground.playgroundSessionMode,
      ],
      () => {
        performSave();
      },
      { delay: 1000 },
    );

    // Cleanup function
    return () => {
      disposed = true;
      saveInProgressRef.current = false;
      hasPendingSaveRef.current = false;
      if (scheduledSaveTimeoutRef.current) {
        clearTimeout(scheduledSaveTimeoutRef.current);
        scheduledSaveTimeoutRef.current = null;
      }

      const cancellationError = new Error("Session save was cancelled.");
      cancellationError.name = "AbortError";
      settleSaveWaiters(cancellationError);

      // Ensure save state is reset when component unmounts
      if (playground.isSavingSession) {
        playground.setIsSavingSession(false);
      }

      // Dispose of reactions
      disposeUndebouncedReaction();
      disposeDebouncedReaction();

      // Clear tracker reference
      playground.setWaitForSessionSaveCompletion(null);
      if (saveCompletionTracker) {
        saveCompletionTracker.current = null;
      }
    };
  }, [
    playground,
    updateSession,
    resetSessionUpdate,
    userToken,
    queryClient,
    saveCompletionTracker,
  ]);
};

export default useAutoSaveSession;
